import { useState } from 'react';
import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { BookOpen24Regular } from '@fluentui/react-icons';
import { SystemGuideModal } from '../components/guide/SystemGuideModal';
import { useT } from '../i18n';

const useStyles = makeStyles({
  root: {
    padding: '24px',
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  heroContainer: {
    padding: '32px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: tokens.shadow4,
  },
  heroTitle: {
    fontSize: '28px',
    fontWeight: '700',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: tokens.colorBrandForeground1,
  },
  heroSubtitle: {
    fontSize: '15px',
    color: tokens.colorNeutralForeground2,
    maxWidth: '700px',
    margin: 0,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  card: {
    padding: '20px',
    borderRadius: '10px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: tokens.shadow8,
      transform: 'translateY(-2px)',
    },
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
  },
  cardDesc: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.4',
  },
});

export function SystemGuidePage() {
  const s = useStyles();
  const t = useT();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('architecture');

  const openTab = (tab: string) => {
    setSelectedTab(tab);
    setModalOpen(true);
  };

  return (
    <div className={s.root}>
      {/* Hero Banner */}
      <div className={s.heroContainer}>
        <h1 className={s.heroTitle}>
          <BookOpen24Regular fontSize={32} />
          <span>{t.systemGuide?.title ?? 'Guía e Instructivo del Sistema Agrovid'}</span>
        </h1>
        <p className={s.heroSubtitle}>
          {t.systemGuide?.subtitle ?? 'Aprende a utilizar el sistema conversacional por Telegram y el Dashboard Web.'}
        </p>
        <div>
          <Button
            appearance="primary"
            size="large"
            icon={<BookOpen24Regular />}
            onClick={() => openTab('architecture')}
          >
            {t.systemGuide?.openGuideBtn ?? 'Abrir Guía del Sistema'}
          </Button>
        </div>
      </div>

      {/* Quick Access Cards */}
      <div className={s.cardsGrid}>
        <div className={s.card} onClick={() => openTab('architecture')}>
          <div className={s.cardTitle}>{t.systemGuide.cards.architecture.title}</div>
          <div className={s.cardDesc}>
            {t.systemGuide.cards.architecture.desc}
          </div>
        </div>

        <div className={s.card} onClick={() => openTab('modules')}>
          <div className={s.cardTitle}>{t.systemGuide.cards.modules.title}</div>
          <div className={s.cardDesc}>
            {t.systemGuide.cards.modules.desc}
          </div>
        </div>

        <div className={s.card} onClick={() => openTab('voiceCommands')}>
          <div className={s.cardTitle}>{t.systemGuide.cards.voice.title}</div>
          <div className={s.cardDesc}>
            {t.systemGuide.cards.voice.desc}
          </div>
        </div>

        <div className={s.card} onClick={() => openTab('roles')}>
          <div className={s.cardTitle}>{t.systemGuide.cards.roles.title}</div>
          <div className={s.cardDesc}>
            {t.systemGuide.cards.roles.desc}
          </div>
        </div>

        <div className={s.card} onClick={() => openTab('faq')}>
          <div className={s.cardTitle}>{t.systemGuide.cards.faq.title}</div>
          <div className={s.cardDesc}>
            {t.systemGuide.cards.faq.desc}
          </div>
        </div>
      </div>

      {/* Interactive Modal Component */}
      <SystemGuideModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initialTab={selectedTab}
      />
    </div>
  );
}
